import React from "react";
import { Modal } from "antd";
import { LuX } from "react-icons/lu";
import image from "../../assets/images/Image (4).png";
import { useSelector } from "react-redux";
import { selectCurrentRole } from "../../store/auth/authSlice";
import { Frown, Meh, SmilePlus, Smile, SmileIcon, Star } from "lucide-react";
import FileResponsePreview from "../common/FileResponsePreview";
import { getRadioListDisplayLines } from "../../utils/radioList";
import {
  hasExplicitSliderSelection,
  isAnonymousExportInput,
} from "../../utils/reportsHelper";

const UserResponseModal = ({
  visible,
  onClose,
  userData,
  userResponses,
  selectedUser,
}) => {
  const userRole = useSelector(selectCurrentRole);

  const getEmoji = (index, totalOptions = 5) => {
    const emojiSets = {
      4: [
        { Icon: Frown, color: "#FF4D4F" },
        { Icon: Meh, color: "#FFA940" },
        { Icon: Smile, color: "#52C41A" },
        { Icon: SmilePlus, color: "#1890FF" },
      ],
      5: [
        { Icon: Frown, color: "#FF4D4F" },
        { Icon: Meh, color: "#FFA940" },
        { Icon: SmileIcon, color: "#FADB14" },
        { Icon: Smile, color: "#52C41A" },
        { Icon: SmilePlus, color: "#1890FF" },
      ],
      7: [
        { Icon: Frown, color: "#FF4D4F" },
        { Icon: Frown, color: "#FF7A45" },
        { Icon: Meh, color: "#FFA940" },
        { Icon: SmileIcon, color: "#FADB14" },
        { Icon: Smile, color: "#73D13D" },
        { Icon: Smile, color: "#52C41A" },
        { Icon: SmilePlus, color: "#1890FF" },
      ],
    };

    const emojiSet = emojiSets[totalOptions.toString()] || emojiSets["5"];

    const emojiData = emojiSet[index] || emojiSet[0];

    return {
      Icon: emojiData.Icon,
      color: emojiData.color,
    };
  };

  const getEmojiDescriptionByIndex = (index, totalOptions = 5) => {
    if (totalOptions === 5) {
      const descriptions = [
        "Very Unsatisfied",
        "Unsatisfied",
        "Neutral",
        "Satisfied",
        "Very Satisfied",
      ];

      return descriptions[index] || `Rating ${index + 1}`;
    } else if (totalOptions === 4) {
      const descriptions = ["Poor", "Fair", "Good", "Excellent"];

      return descriptions[index] || `Rating ${index + 1}`;
    } else if (totalOptions === 7) {
      const descriptions = [
        "Extremely Poor",
        "Very Poor",
        "Poor",
        "Neutral",
        "Good",
        "Very Good",
        "Excellent",
      ];

      return descriptions[index] || `Rating ${index + 1}`;
    }

    return `Rating ${index + 1} of ${totalOptions}`;
  };

  const getEmojiIndexFromValue = (value, totalOptions = 5) => {
    if (typeof value !== "number" || isNaN(value)) return null;

    const normalizedIndex = Math.round((value / 100) * (totalOptions - 1));

    return Math.max(0, Math.min(normalizedIndex, totalOptions - 1));
  };

  const getSliderNumericDisplay = (rawValue, emojiIndex, totalOptions) => {
    let v = rawValue;

    if (
      v &&
      typeof v === "object" &&
      "value" in v &&
      v.value !== undefined &&
      v.value !== null
    ) {
      const inner = v.value;

      if (typeof inner === "number" && !Number.isNaN(inner)) {
        v = inner;
      } else if (typeof inner === "string" && inner.trim() !== "") {
        const n = Number(inner);

        v = Number.isNaN(n) ? v : n;
      }
    }

    const parsedValue =
      typeof v === "number"
        ? v
        : typeof v === "string" && v.trim() !== ""
        ? Number(v)
        : null;

    if (typeof parsedValue === "number" && !Number.isNaN(parsedValue)) {
      const rounded = Math.round(parsedValue);

      // 0-based index persisted as numeric (0..N-1)
      if (
        Number.isInteger(rounded) &&
        rounded >= 0 &&
        rounded < totalOptions &&
        totalOptions > 1
      ) {
        return `${rounded + 1}/${totalOptions}`;
      }

      // 1-based point scale persisted as numeric (1..N)
      if (
        Number.isInteger(rounded) &&
        rounded >= 1 &&
        rounded <= totalOptions &&
        totalOptions > 1
      ) {
        return `${rounded}/${totalOptions}`;
      }

      // True percentage slider value
      if (rounded >= 0 && rounded <= 100) {
        return `${rounded}/100`;
      }
    }

    // Fallback to derived option index
    return `${emojiIndex + 1}/${totalOptions}`;
  };

  /** Answers stored as `{ selection, value }` (e.g. dropdown + free text). */
  const formatSelectionValuePayload = (payload) => {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      return null;
    }
    if (!("selection" in payload) && !("value" in payload)) {
      return null;
    }
    const selection =
      payload.selection !== undefined && payload.selection !== null
        ? String(payload.selection).trim()
        : "";
    const innerValue =
      payload.value !== undefined && payload.value !== null
        ? String(payload.value).trim()
        : "";
    if (selection && innerValue) {
      return `${selection} (${innerValue})`;
    }
    if (selection) return selection;
    if (innerValue) return innerValue;
    return "";
  };

  const isSliderLikeEntry = (item) => hasExplicitSliderSelection(item);

  const formatSliderStyleEntry = (item) => {
    const label =
      item.label !== undefined && item.label !== null
        ? String(item.label).trim()
        : "";
    const emojiIndexRaw = item.emojiIndex;
    let emojiIndex = 0;
    if (typeof emojiIndexRaw === "number" && !Number.isNaN(emojiIndexRaw)) {
      emojiIndex = emojiIndexRaw;
    } else if (
      typeof emojiIndexRaw === "string" &&
      emojiIndexRaw.trim() !== ""
    ) {
      const n = Number.parseInt(emojiIndexRaw, 10);
      if (!Number.isNaN(n)) emojiIndex = n;
    }
    const totalRaw = item.totalOptions;
    let totalOptions = 5;
    if (typeof totalRaw === "number" && totalRaw > 0 && !Number.isNaN(totalRaw)) {
      totalOptions = totalRaw;
    } else if (typeof totalRaw === "string" && totalRaw.trim() !== "") {
      const n = Number.parseInt(totalRaw, 10);
      if (!Number.isNaN(n) && n > 0) totalOptions = n;
    }
    const numeric = getSliderNumericDisplay(
      item.value,
      emojiIndex,
      totalOptions,
    );
    if (label && numeric) return `${label} (${numeric})`;
    if (label) return label;
    if (numeric) return numeric;
    return String(item.value);
  };

  const formatSliderStyleArray = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    if (!arr.every(isSliderLikeEntry)) return null;
    return arr.map((item) => formatSliderStyleEntry(item)).join(", ");
  };

  const formatSliderStyleValue = (parsed) => {
    if (Array.isArray(parsed)) {
      const text = formatSliderStyleArray(parsed);
      return text != null ? text : null;
    }
    if (isSliderLikeEntry(parsed)) {
      return formatSliderStyleEntry(parsed);
    }
    return null;
  };

  /** Recover common malformed JSON (missing quote before totalOptions key). */
  const tryParseDisplayJson = (trimmed) => {
    try {
      return JSON.parse(trimmed);
    } catch {
      try {
        const fixed = trimmed.replace(/,\s*totalOptions"\s*:/g, ', "totalOptions":');
        if (fixed !== trimmed) {
          return JSON.parse(fixed);
        }
      } catch {
        /* ignore */
      }
      return null;
    }
  };

  const formatDisplayValue = (value, fallback = "No response") => {
    if (React.isValidElement(value)) return value;

    if (value === null || value === undefined) return fallback;

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (
        (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))
      ) {
        try {
          const parsed = tryParseDisplayJson(trimmed);
          if (parsed !== null) {
            const fromSlider = formatSliderStyleValue(parsed);
            if (fromSlider != null) {
              return fromSlider || fallback;
            }
            const fromPayload = formatSelectionValuePayload(parsed);
            if (fromPayload !== null) {
              return fromPayload || fallback;
            }
          }
        } catch {
          // not JSON, fall through
        }
      }
      if (trimmed === "") return fallback;
    }

    if (typeof value === "object" && !Array.isArray(value)) {
      const fromSlider = formatSliderStyleValue(value);
      if (fromSlider != null) {
        return fromSlider || fallback;
      }
      const fromPayload = formatSelectionValuePayload(value);
      if (fromPayload !== null) {
        return fromPayload || fallback;
      }
    }

    if (Array.isArray(value)) {
      const fromSlider = formatSliderStyleArray(value);
      if (fromSlider != null) {
        return fromSlider || fallback;
      }
      const primitiveValues = value.filter(
        (item) =>
          typeof item === "string" ||
          typeof item === "number" ||
          typeof item === "boolean",
      );

      if (primitiveValues.length) {
        return primitiveValues.join(", ");
      }

      const fromObjects = value
        .map((item) => {
          if (item && typeof item === "object") {
            return formatDisplayValue(item, "");
          }

          if (typeof item === "string") {
            return formatDisplayValue(item, "");
          }

          return null;
        })
        .filter((s) => s != null && s !== "");

      if (fromObjects.length) {
        return fromObjects.join(", ");
      }

      return JSON.stringify(value);
    }

    if (typeof value === "object" && value !== null) {
      if (
        "value" in value &&
        value.value !== undefined &&
        value.value !== null
      ) {
        const inner = value.value;

        if (typeof inner === "number" && !Number.isNaN(inner)) {
          return String(inner);
        }

        if (typeof inner === "string" && inner.trim() !== "") {
          const n = Number(inner);

          if (!Number.isNaN(n)) return String(n);

          return inner;
        }

        if (typeof inner === "object" && inner !== null && "value" in inner) {
          return formatDisplayValue(inner, fallback);
        }
      }

      if (
        typeof value.label === "string" &&
        value.label &&
        (value.value === undefined || value.value === null)
      ) {
        return value.label;
      }

      return JSON.stringify(value);
    }

    if (typeof value === "string") {
      const trimmed = value.trim();

      if (
        (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
        (trimmed.startsWith("{") && trimmed.endsWith("}"))
      ) {
        try {
          const parsed = JSON.parse(trimmed);

          return formatDisplayValue(parsed, fallback);
        } catch {
          // not JSON, show as-is below
        }
      }

      return String(value);
    }

    return String(value);
  };

  const formatResponses = () => {
    const sortedUserResponses = [...userResponses].sort((a, b) => {
      const stepA = a.step || JSON.parse(a.response)?.step || 0;
      const stepB = b.step || JSON.parse(b.response)?.step || 0;

      return stepA - stepB;
    });

    const allResponses = [];

    sortedUserResponses.forEach((response, responseIndex) => {
      try {
        const parsedResponse = JSON.parse(response.response);
        const responseValues = parsedResponse.responses || {};
        const questionData = response.questionData;
        const step = parsedResponse.step || response.step;

        const responseAssignment = response.response_assignments?.find(
          (assignment) => assignment.question === response.question,
        );

        let formattedItems = [];

        if (!questionData) {
          Object.keys(responseValues).forEach((key, keyIndex) => {
            const value = responseValues[key];
            let formattedValue = "No response";

            if (value.type === "slider" && value.value !== undefined) {
              if (
                !hasExplicitSliderSelection(value.value) &&
                !hasExplicitSliderSelection(value)
              ) {
                formattedValue = "No response";
              } else {
              const sliderValue = value.value;
              let emojiIndex, totalOptions;
              const showStars = value.show_stars || false;

              if (value.emojiIndex !== undefined && value.totalOptions) {
                emojiIndex = value.emojiIndex;
                totalOptions = value.totalOptions;
              } else {
                totalOptions = 5;
                emojiIndex = getEmojiIndexFromValue(
                  sliderValue.value,
                  totalOptions,
                );
              }

              const numericDisplay = getSliderNumericDisplay(
                sliderValue,
                emojiIndex,
                totalOptions,
              );

              const description =
                value.label ||
                getEmojiDescriptionByIndex(emojiIndex, totalOptions);

              if (emojiIndex == null) {
                formattedValue = "No response";
              } else if (showStars) {
                const yellowColor = "#FFD700";

                formattedValue = (
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: totalOptions }, (_, idx) => (
                          <Star
                            key={idx}
                            size={20}
                            color={yellowColor}
                            fill={idx <= emojiIndex ? yellowColor : "none"}
                            strokeWidth={2}
                          />
                        ))}
                      </div>
                      <span>{description}</span>
                    </div>
                    <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium">
                      {numericDisplay}
                    </span>
                  </div>
                );
              } else {
                const { Icon, color } = getEmoji(emojiIndex, totalOptions);

                formattedValue = (
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                      <div className="mr-2">
                        <Icon size={20} color={color} />
                      </div>
                      <span>{description}</span>
                    </div>
                    <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium">
                      {numericDisplay}
                    </span>
                  </div>
                );
              }
              }
            } else if (value.type === "date" && value.value) {
              try {
                const dateObj = new Date(value.value);

                if (!isNaN(dateObj.getTime())) {
                  const options = {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  };

                  formattedValue = dateObj.toLocaleDateString(
                    undefined,
                    options,
                  );
                } else {
                  formattedValue = value.value;
                }
              } catch (e) {
                formattedValue = value.value;
              }
            } else if (value.type === "record-video") {
              if (
                userRole === "organization" &&
                responseAssignment?.translation &&
                responseAssignment.translation.length > 0
              ) {
                formattedValue =
                  responseAssignment.translation[0].text ||
                  "No translation available";
              } else if (
                value.value &&
                typeof value.value === "string" &&
                value.value.startsWith("http")
              ) {
                formattedValue = (
                  <div className="flex items-center">
                    <a
                      href={value.value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      View Video
                    </a>
                    {responseAssignment?.translation &&
                      responseAssignment.translation[0] && (
                        <p className="ml-4">
                          {responseAssignment.translation[0].text}
                        </p>
                      )}
                  </div>
                );
              } else {
                formattedValue = "No video recorded";
              }
            } else if (value.value !== undefined && value.value !== null) {
              formattedValue = formatDisplayValue(value.value);
            }

            const fallbackTitle =
              key.replace(`question_${response.question}_`, "") || "Input";

            if (
              isAnonymousExportInput(
                {
                  title: fallbackTitle,
                  inputTitle: fallbackTitle,
                  type: value.type,
                },
                formattedValue,
                value?.value,
              )
            ) {
              return;
            }

            formattedItems.push({
              srNo: `${step + 1}.${formattedItems.length + 1}`,
              question: `Question ${step + 1}`,
              inputTitle: fallbackTitle,
              inputType: value.type || "Unknown",
              response: formattedValue,
              step: step,
              originalIndex: responseIndex,
            });
          });

          if (formattedItems.length === 0) {
            formattedItems = [
              {
                srNo: `${step + 1}.1`,
                question: `Question ${step + 1}`,
                inputTitle: "Missing question data",
                response: "Data unavailable",
                step: step,
                originalIndex: responseIndex,
              },
            ];
          }
        } else {
          let inputIndex = formattedItems.length;
          const usedResponseKeys = new Set();

          questionData.response.forEach((inputField) => {
            let inputResponse = responseValues[inputField.input_title];
            let matchedKey = inputResponse ? inputField.input_title : undefined;

            if (!inputResponse) {
              const byInputIdKey = Object.keys(responseValues).find((key) => {
                const val = responseValues[key];

                return (
                  !usedResponseKeys.has(key) &&
                  val &&
                  val.inputId === inputField._id
                );
              });

              if (byInputIdKey) {
                inputResponse = responseValues[byInputIdKey];
                matchedKey = byInputIdKey;
              }
            }

            if (!inputResponse) {
              if (
                inputField.input_type === "email" &&
                responseValues["Email - mandatory"]
              ) {
                inputResponse = responseValues["Email - mandatory"];
                matchedKey = "Email - mandatory";
              } else if (
                inputField.input_type === "name" &&
                responseValues["Name - optional"]
              ) {
                inputResponse = responseValues["Name - optional"];
                matchedKey = "Name - optional";
              }
            }

            if (!inputResponse) {
              const byTypeKey = Object.keys(responseValues).find((key) => {
                if (usedResponseKeys.has(key)) return false;

                const val = responseValues[key];

                return (
                  val &&
                  val.type === inputField.input_type &&
                  val.value !== undefined
                );
              });

              if (byTypeKey) {
                inputResponse = responseValues[byTypeKey];
                matchedKey = byTypeKey;
              }
            }

            if (!inputResponse || inputResponse.value === undefined) {
              return;
            }

            if (matchedKey) {
              usedResponseKeys.add(matchedKey);
            }

            let formattedResponse = "No response";

            if (inputResponse) {
              switch (inputField.input_type) {
                case "checkbox":
                  formattedResponse = formatDisplayValue(inputResponse.value);
                  break;
                case "date":
                  if (
                    inputResponse.value &&
                    typeof inputResponse.value === "string"
                  ) {
                    try {
                      const dateObj = new Date(inputResponse.value);

                      if (!isNaN(dateObj.getTime())) {
                        const options = {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        };

                        formattedResponse = dateObj.toLocaleDateString(
                          undefined,
                          options,
                        );
                      } else {
                        formattedResponse = inputResponse.value;
                      }
                    } catch (e) {
                      formattedResponse = inputResponse.value;
                    }
                  } else {
                    formattedResponse = "No date selected";
                  }
                  break;
                case "file":
                case "multiple-files": {
                  formattedResponse = (
                    <FileResponsePreview
                      value={inputResponse.value}
                      fileTypeHint={
                        inputField.file_type || inputField.fileType
                      }
                    />
                  );
                  break;
                }
                case "record-video":
                  if (
                    userRole === "organization" &&
                    responseAssignment?.translation &&
                    responseAssignment.translation.length > 0
                  ) {
                    formattedResponse =
                      responseAssignment.translation[0].text ||
                      "No translation available";
                  } else if (
                    inputResponse.value &&
                    typeof inputResponse.value === "string" &&
                    inputResponse.value.startsWith("http")
                  ) {
                    formattedResponse = (
                      <div className="flex items-center">
                        {userRole === "admin" && (
                          <a
                            href={inputResponse.value}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            View Video
                          </a>
                        )}
                        {responseAssignment?.translation &&
                        responseAssignment.translation[0] ? (
                          <p className="ml-4">
                            {responseAssignment.translation[0]?.text}
                          </p>
                        ) : (
                          <span className="px-1">No translation</span>
                        )}
                      </div>
                    );
                  } else {
                    formattedResponse = "No video recorded";
                  }
                  break;
                case "slider":
                  if (
                    !hasExplicitSliderSelection(inputResponse.value) &&
                    !hasExplicitSliderSelection(inputResponse)
                  ) {
                    formattedResponse = "No response";
                    break;
                  }

                  const showStars =
                    inputField.show_stars ||
                    inputField.slider_config?.show_stars ||
                    false;

                  let emojiIndex, totalOptions;

                  if (
                    inputResponse.emojiIndex !== undefined &&
                    inputResponse.totalOptions
                  ) {
                    emojiIndex = inputResponse.emojiIndex;
                    totalOptions = inputResponse.totalOptions;
                  } else if (inputField.input_options && inputResponse.label) {
                    emojiIndex = inputField.input_options.indexOf(
                      inputResponse.label,
                    );
                    totalOptions = inputField.input_options.length;

                    if (emojiIndex === -1) {
                      formattedResponse = "No response";
                      break;
                    }
                  } else {
                    const sliderValue = inputResponse.value;
                    totalOptions =
                      inputField.input_options?.length ||
                      parseInt(inputField.scale_type || "5", 10);
                    emojiIndex = getEmojiIndexFromValue(
                      typeof sliderValue === "object"
                        ? sliderValue?.value
                        : sliderValue,
                      totalOptions,
                    );
                  }

                  if (emojiIndex == null) {
                    formattedResponse = "No response";
                    break;
                  }

                  const numericDisplay = getSliderNumericDisplay(
                    inputResponse.value,
                    emojiIndex,
                    totalOptions,
                  );

                  const description =
                    inputResponse.label ||
                    (inputField.input_options &&
                      inputField.input_options[emojiIndex]) ||
                    getEmojiDescriptionByIndex(emojiIndex, totalOptions);

                  if (showStars) {
                    const yellowColor = "#FFD700";

                    formattedResponse = (
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: totalOptions }, (_, idx) => (
                              <Star
                                key={idx}
                                size={20}
                                color={yellowColor}
                                fill={idx <= emojiIndex ? yellowColor : "none"}
                                strokeWidth={2}
                              />
                            ))}
                          </div>
                          <span>{description}</span>
                        </div>
                        <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium">
                          {numericDisplay}
                        </span>
                      </div>
                    );
                  } else {
                    const { Icon, color } = getEmoji(emojiIndex, totalOptions);

                    formattedResponse = (
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center">
                          <div className="mr-2">
                            <Icon size={20} color={color} />
                          </div>
                          <span>{description}</span>
                        </div>
                        <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium">
                          {numericDisplay}
                        </span>
                      </div>
                    );
                  }
                  break;
                case "radio":
                  formattedResponse = formatDisplayValue(inputResponse.value);
                  break;
                case "checkbox-list":
                case "radio-list": {
                  const lines = getRadioListDisplayLines(inputResponse.value);
                  formattedResponse = lines.length ? (
                    <div className="whitespace-pre-line leading-5">
                      {lines.join("\n")}
                    </div>
                  ) : (
                    "No response"
                  );
                  break;
                }
                case "name":
                case "email":
                case "text":
                case "number":
                case "paragraph":
                  formattedResponse = formatDisplayValue(inputResponse.value);
                  break;
                default:
                  formattedResponse = formatDisplayValue(inputResponse.value);
              }
            }

            if (
              isAnonymousExportInput(
                {
                  title: inputField.input_title,
                  inputTitle: inputField.input_title,
                  type: inputField.input_type,
                },
                formattedResponse,
                inputResponse?.value,
              )
            ) {
              return;
            }

            formattedItems.push({
              srNo: `${step + 1}.${++inputIndex}`,
              question: questionData.title || `Question ${step + 1}`,
              inputTitle: inputField.input_title || "Question",
              inputType: inputField.input_type,
              response: formattedResponse,
              step: step,
              originalIndex: responseIndex,
            });
          });
        }

        allResponses.push(...formattedItems);
      } catch (e) {
        const step = response.step || 0;

        allResponses.push({
          srNo: `${step + 1}.1`,
          question:
            (response.questionData && response.questionData.title) ||
            `Question ${step + 1}`,
          inputTitle: "Error parsing response",
          response: "Invalid Response",
          step: step,
          originalIndex: responseIndex,
        });
      }
    });

    return allResponses.sort((a, b) => {
      if (a.step !== b.step) {
        return a.step - b.step;
      }

      return a.originalIndex - b.originalIndex;
    });
  };

  const responses = formatResponses();
  const userDisplayName =
    typeof userData?.name === "string" && userData.name.trim()
      ? userData.name
      : "User";

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      className="responses-modal"
      closeIcon={
        <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      title={
        <div className="flex items-center gap-3 mb-6">
          <h3 className="text-xl font-semibold m-0">User Responses</h3>
          <div>
            <div className="flex items-center gap-2 border rounded-full p-1">
              <img
                src={image || image}
                alt={userDisplayName}
                className="w-10 h-10 rounded-full"
              />
              <div>
                <p className="text-xs text-black m-0 pr-1">{userDisplayName}</p>
                <p className="text-xs text-lightBlack m-0 pr-1">
                  {userData?.email}
                </p>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="bg-[#F9FAFB] rounded-xl">
        <div className="grid grid-cols-12 px-4 py-3 border-b text-sm font-medium text-gray-600">
          <div className="col-span-1">Sr#</div>
          <div className="col-span-3">Question</div>
          <div className="col-span-3">Input Field</div>
          <div className="col-span-5">Response</div>
        </div>
        <div className="divide-y">
          {responses.map((item, index) => (
            <div
              key={`${item.srNo}-${index}`}
              className="grid grid-cols-12 px-4 py-4 text-sm gap-4"
            >
              <div className="col-span-1 text-gray-500">{item.srNo}</div>
              <div className="col-span-3 text-gray-900">
                {formatDisplayValue(item.question, "Question")}
              </div>
              <div className="col-span-3 text-gray-700">
                {formatDisplayValue(item.inputTitle, "Input field")}
              </div>
              <div className="col-span-5 text-gray-600">
                {formatDisplayValue(item.response)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default UserResponseModal;
